import { timeToMinutes } from './timeFormat';

const DAY_MINUTES = 24 * 60;

/** Minutes since local midnight for the current clock time. */
export function currentLocalTimeMinutes(now: Date = new Date()): number {
  return now.getHours() * 60 + now.getMinutes();
}

/** Expand a dynamic schedule time range so the current time is never clipped. */
export function ensureTimeRangeIncludesNow(
  earliestTime: number,
  latestTime: number,
  nowMinutes: number = currentLocalTimeMinutes()
): { earliestTime: number; latestTime: number } {
  const snappedNow = Math.max(0, Math.min(DAY_MINUTES, Math.floor(nowMinutes / 30) * 30));
  let earliest = earliestTime;
  let latest = latestTime;
  if (snappedNow < earliest) {
    earliest = Math.max(0, snappedNow - 60);
  }
  if (snappedNow > latest) {
    latest = Math.min(DAY_MINUTES, snappedNow + 60);
  }
  return { earliestTime: earliest, latestTime: latest };
}

function scheduleAnchorScrollTop(
  scrollEl: HTMLElement,
  anchorMinutes: number,
  earliestTime: number,
  latestTime: number,
  options?: {
    slotMinutes?: number;
    pxPerSlot?: number;
    paddingMinutesAbove?: number;
    centerInViewport?: boolean;
  }
): number {
  const slotMinutes = options?.slotMinutes ?? 30;
  const pxPerSlot = options?.pxPerSlot ?? 40;
  const totalRange = latestTime - earliestTime;
  if (totalRange <= 0) return 0;
  const gridPx = (totalRange / slotMinutes) * pxPerSlot;
  const anchorPx = ((anchorMinutes - earliestTime) / totalRange) * gridPx;
  const desired = options?.centerInViewport
    ? anchorPx - scrollEl.clientHeight / 2
    : Math.max(
        0,
        anchorPx - ((options?.paddingMinutesAbove ?? 60) / totalRange) * gridPx
      );
  const maxScroll = Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight);
  return Math.min(Math.max(0, desired), maxScroll);
}

/** Earliest start time among active lessons, optionally including a preview slot. */
export function earliestActiveLessonStartMinutes(
  lessons: { isActive?: boolean; startTime: string }[],
  preview?: { startTime?: string } | null
): number | null {
  let min: number | null = null;
  for (const l of lessons) {
    if (l.isActive === false) continue;
    const s = timeToMinutes(l.startTime);
    if (min === null || s < min) min = s;
  }
  if (preview?.startTime) {
    const s = timeToMinutes(preview.startTime);
    if (min === null || s < min) min = s;
  }
  return min;
}

/**
 * Vertical scroll offset so the anchor time sits below the top with padding (matches grid: 40px per 30min slot).
 */
export function scheduleScrollTopForAnchor(
  scrollEl: HTMLElement,
  anchorMinutes: number,
  earliestTime: number,
  latestTime: number,
  options?: { slotMinutes?: number; pxPerSlot?: number; paddingMinutesAbove?: number }
): number {
  return scheduleAnchorScrollTop(scrollEl, anchorMinutes, earliestTime, latestTime, options);
}

/** Vertical scroll offset so the anchor time is centered in the viewport. */
export function scheduleScrollTopToCenterAnchor(
  scrollEl: HTMLElement,
  anchorMinutes: number,
  earliestTime: number,
  latestTime: number,
  options?: { slotMinutes?: number; pxPerSlot?: number }
): number {
  return scheduleAnchorScrollTop(scrollEl, anchorMinutes, earliestTime, latestTime, {
    ...options,
    centerInViewport: true,
  });
}
